import { useEffect, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthMethods, useCurrentUser, useSignIn } from "../../backend/queries/auth";
import { authApi } from "../../backend/api/auth";
import { AuthLayout } from "./auth-layout";
import { GoogleLogo, MicrosoftLogo } from "./provider-logos";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

type FieldErrors = {
  email?: string;
  password?: string;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (email: string, password: string): FieldErrors => {
  const errors: FieldErrors = {};
  const trimmedEmail = email.trim();

  if (!trimmedEmail) {
    errors.email = "Enter your email address.";
  } else if (!EMAIL_PATTERN.test(trimmedEmail)) {
    errors.email = "Enter a valid email address.";
  }

  if (!password) {
    errors.password = "Enter your password.";
  }

  return errors;
};

const routeForRole = (role?: string | null) =>
  (role === "Owner" || role === "Admin") || role === "Agent" ? "/dashboard" : "/home";

const safeReturnPath = (state: unknown): string | null => {
  if (!state || typeof state !== "object" || !("from" in state)) return null;
  const from = (state as { from?: unknown }).from;
  return typeof from === "string" &&
    from.startsWith("/") &&
    !from.startsWith("//")
    ? from
    : null;
};

const Login = (): JSX.Element => {
  const methods = useAuthMethods();
  const available = (provider: string) => methods.data?.providers.some(item => item.provider === provider && item.configured) === true;
  const [email, setEmail] = useState("");
  const [step, setStep] = useState<"email" | "signin">("email");
  const [pwd, setPwd] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [errorText, setErrorText] = useState(
    new URLSearchParams(window.location.search).has("provider_error")
      ? "We couldn't complete sign-in. Check that your administrator has approved your email, then choose the matching Google account. Use your invitation for Microsoft or password setup."
      : "",
  );
  const [submitting, setSubmitting] = useState(false);
  const { data: currentUser } = useCurrentUser();
  const signIn = useSignIn();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = safeReturnPath(location.state);

  useEffect(() => {
    if (currentUser) {
      navigate(
        currentUser.profile?.password_setup_required ? "/set-password" : returnPath ||
          routeForRole(currentUser.role || currentUser.profile?.role),
        {
          replace: true,
        },
      );
    }
  }, [currentUser, navigate, returnPath]);

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setFieldErrors((current) =>
      current.email ? { ...current, email: undefined } : current,
    );
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPwd(event.target.value);
    setFieldErrors((current) =>
      current.password ? { ...current, password: undefined } : current,
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorText("");

    const errors = validate(email, step === "email" ? "unused" : pwd);
    setFieldErrors(errors);
    if (errors.email || errors.password) return;

    if (step === "email") {
      setEmail(email.trim());
      setStep("signin");
      return;
    }

    setSubmitting(true);

    try {
      const data = await signIn.mutateAsync({
        email: email.trim(),
        password: pwd,
      });
      setSubmitting(false);
      navigate(data.user.profile?.password_setup_required ? "/set-password" : returnPath || routeForRole(data.user.user_metadata?.user_role), {
        replace: true,
      });
    } catch {
      setSubmitting(false);
      setErrorText("Incorrect email or password.");
    }
  };

  const startProviderSignIn = (provider: "microsoft" | "google") => {
    window.location.assign(
      authApi.providerSignInUrl(provider, returnPath || undefined),
    );
  };

  return (
    <AuthLayout>
        <Card>
          <CardHeader className="space-y-1.5 pb-5 text-center">
            <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
            <CardDescription className="text-balance">
              Sign in to manage your relocation projects, tasks, documents, and
              budgets.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} aria-busy={submitting} noValidate>
              <div className="flex flex-col gap-6">
                <div className="grid gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                    <Label htmlFor="username">Email</Label>
                    {step === "signin" && (
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 text-sm"
                        onClick={() => {
                          setStep("email");
                          setPwd("");
                          setErrorText("");
                          setFieldErrors({});
                        }}
                      >
                        Use a different email
                      </Button>
                    )}
                  </div>
                  <Input
                    readOnly={step === "signin"}
                    type="email"
                    placeholder="name@example.com"
                    id="username"
                    autoComplete="username"
                    required
                    value={email}
                    aria-invalid={Boolean(fieldErrors.email)}
                    aria-describedby={
                      fieldErrors.email ? "username-error" : undefined
                    }
                    onChange={handleEmailChange}
                  />
                  {fieldErrors.email && (
                    <p id="username-error" className="text-sm text-destructive">
                      {fieldErrors.email}
                    </p>
                  )}
                </div>

                {step === "signin" && (
                  <>
                    <div className="grid gap-2">
                      <div className="flex items-center">
                        <Label htmlFor="password">Password</Label>
                        <Button
                          asChild
                          variant="link"
                          className="ml-auto h-auto p-0 text-sm"
                        >
                          <Link to="/reset-password">Forgot password?</Link>
                        </Button>
                      </div>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Password"
                          id="password"
                          autoComplete="current-password"
                          required
                          value={pwd}
                          className="pr-10"
                          aria-invalid={Boolean(fieldErrors.password)}
                          aria-describedby={
                            fieldErrors.password ? "password-error" : undefined
                          }
                          onChange={handlePasswordChange}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-10 w-10"
                          onClick={() => setShowPassword((value) => !value)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                          <span className="sr-only">
                            Toggle password visibility
                          </span>
                        </Button>
                      </div>
                      {fieldErrors.password && (
                        <p
                          id="password-error"
                          className="text-sm text-destructive"
                        >
                          {fieldErrors.password}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {errorText && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Sign in failed</AlertTitle>
                    <AlertDescription>{errorText}</AlertDescription>
                  </Alert>
                )}

                <div className="grid gap-3">
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting
                      ? "Please wait..."
                      : step === "email"
                        ? "Next"
                        : "Sign in"}
                  </Button>

                </div>

                <div className="grid gap-4">
                  <div className="relative text-center text-xs text-muted-foreground before:absolute before:inset-x-0 before:top-1/2 before:border-t">
                    <span className="relative bg-card px-2 uppercase tracking-wide">
                      or
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={submitting || !available("microsoft")}
                      onClick={() => startProviderSignIn("microsoft")}
                    >
                      <MicrosoftLogo className="mr-2 h-4 w-4 shrink-0" />
                      Continue with Microsoft
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={submitting || !available("google")}
                      onClick={() => startProviderSignIn("google")}
                    >
                      <GoogleLogo className="mr-2 h-4 w-4 shrink-0" />
                      Continue with Google
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
    </AuthLayout>
  );
};

export default Login;
